import React, { useRef } from 'react'
import { useSize } from 'components/Charts_Assets'
import { scaleLinear, ScaleLinear } from 'd3-scale'
import { Marker } from 'components/Marker'
import styled from 'styled-components'
const isMobile = window.innerWidth < 712

const textOffset = 5
type ChartProps = {
  data: { v: number; key: number }[][]
  marked: { data: UserInputTypes; label: string | JSX.Element }
  maxKey: number
  yProp?: string
  xDomain: [number, number]
  className: string
  yTicks?: { text?: number[]; lines?: number[] }
  xTicks?: { text?: string[]; lines?: string[] }
  inverted?: boolean
  height: number
  unit?: string
}
export const Bars: React.FC<ChartProps> = ({
  data,
  marked,
  maxKey,
  yProp = 'v',
  height = 40,
  xDomain = [0, 1],
  className,
}) => {
  const [container, size] = useSize()
  const svg = useRef<SVGSVGElement>(null)
  //
  const width = size.width
  const gutterTop = 60
  const gutterRight = isMobile ? 50 : 0
  const gutterLeft = 1
  const gutterBottom = 20
  const plotArea = {
    top: gutterTop,
    right: width - gutterRight,
    left: gutterLeft,
    bottom: height - gutterBottom,
  }
  const barWidth = (plotArea.bottom - plotArea.top) / maxKey

  const xAxisScale = React.useMemo(
    () =>
      scaleLinear<number, number>()
        .domain(xDomain)
        .range([plotArea.left, (plotArea.right - plotArea.left) / 2]),
    [size]
  )

  const yAxisScale = React.useMemo<ScaleLinear<number, number>>(
    () =>
      scaleLinear<number, number>()
        .domain([maxKey, 0])
        .range([plotArea.top, plotArea.bottom]),
    [size]
  )

  const overMax = {
    m: data[0].filter((f) => f.key > maxKey),
    f: data[1].filter((f) => f.key > maxKey),
  }
  const { gender } = marked.data
  let markerData: { y?: number; x?: number } = {}
  if (
    marked &&
    marked.data &&
    (marked.data.perweek || marked.data.perweek === 0)
  ) {
    let barlength =
      (marked.data.perweek || marked.data.perweek === 0) &&
      marked.data.perweek > maxKey
        ? gender === 'f'
          ? overMax.f[0].v
          : overMax.m[0].v
        : gender === 'f'
        ? data[1].filter((f) => f.key === marked.data.perweek)[0]?.v || 0
        : data[0].filter((f) => f.key === marked.data.perweek)[0]?.v || 0

    markerData = {
      y:
        marked.data.perweek || marked.data.perweek === 0
          ? marked.data.perweek > maxKey
            ? gutterTop - 44
            : yAxisScale(marked.data.perweek) || 0
          : 0,
      x:
        gender === 'f'
          ? size.width / 2 +
            30 +
            (xAxisScale(barlength) || 0) -
            ((xAxisScale(1) || 0) / 2 || 0)
          : size.width / 2 - 30 - (xAxisScale(barlength) || 0),
    }
  }

  const roundToNearest = (x: number, v: number) => Math.ceil(x / v) * v

  const getYTicks = (max: number) => {
    let steps = 5
    let rounded = roundToNearest(max, steps)
    return [...Array(rounded / steps + 1)].map((m, i) => i * steps)
  }

  const getXTicks = (max: number, negative?: boolean) => {
    let ticks = []
    let tickval = isMobile ? 2500 : 2500
    for (let i = 1; i < Math.ceil(max / tickval); i++) {
      if (negative) {
        ticks.push(-i * tickval)
      } else {
        ticks.push(i * tickval)
      }
    }
    return ticks
  }
  return (
    <StyledBars className={className}>
      <div
        ref={container}
        style={{ position: 'relative', width: '100%', height: height }}
      >
        <svg
          ref={svg}
          height={height}
          style={{ backgroundColor: 'transparent' }}
        >
          <MaleBars
            transform={`translate(${size.width / 2 - 30},0)`}
            className='maleBars'
          >
            <YTicks
              data={{
                text: getYTicks(maxKey),
                lines: getYTicks(maxKey),
              }}
              x={[-15, plotArea.left - 2]}
              y={yAxisScale}
            />
            <YTicks
              data={{
                text: [],
                lines: [0],
              }}
              x={[size.width, plotArea.left - 2]}
              y={yAxisScale}
            />
            <XTicks
              data={{
                text: getXTicks(xDomain[1], true),
                lines: getXTicks(xDomain[1], true),
              }}
              x={xAxisScale}
              y={[plotArea.top, plotArea.bottom]}
            />
            {data.length > 0 && yProp && (
              <>
                {data[0]
                  .filter((f) => f.key <= maxKey)
                  .map((bar, i) => {
                    let width = xAxisScale(bar.v) || 0
                    if (width < 0) return null
                    return (
                      <rect
                        key={i}
                        width={width}
                        height={barWidth}
                        y={(yAxisScale(bar.key) || 0) - barWidth / 2}
                        x={-width}
                        className={'bar _' + (i % 2)}
                      />
                    )
                  })}
              </>
            )}
            <XTicks
              data={{
                text: [],
                lines: [0],
              }}
              x={xAxisScale}
              y={[plotArea.top - barWidth, plotArea.bottom + barWidth]}
            />
            {
              // OVER MAXIMUM
              <g transform={`translate(30,${gutterTop - 35})`}>
                <text className='ticktext' textAnchor='middle'>
                  {maxKey}+
                </text>

                <g transform={`translate(-30,0)`}>
                  <XTicks
                    data={{
                      text: [],
                      lines: getXTicks(xDomain[1], true),
                    }}
                    x={xAxisScale}
                    y={[-20, 10]}
                  />
                </g>
                <g transform={`translate(30,0)`}>
                  <XTicks
                    data={{
                      text: [],
                      lines: getXTicks(xDomain[1]),
                    }}
                    x={xAxisScale}
                    y={[-20, 10]}
                  />
                </g>
                {overMax.m && overMax.m[0] && (
                  <MaleBars>
                    <rect
                      width={Math.abs(xAxisScale(overMax.m[0].v) || 0)}
                      height={barWidth}
                      y={-barWidth}
                      x={-30 - (xAxisScale(overMax.m[0].v) || 0)}
                      className={'bar'}
                    />
                  </MaleBars>
                )}
                {overMax.f && overMax.f[0] && (
                  <FemaleBars>
                    <rect
                      width={Math.abs(xAxisScale(overMax.f[0].v) || 0)}
                      height={barWidth}
                      y={-barWidth}
                      x={30}
                      className={'bar'}
                    />
                  </FemaleBars>
                )}
                <g transform={`translate(-30,0)`}>
                  <XTicks
                    data={{
                      text: [],
                      lines: [0],
                    }}
                    x={xAxisScale}
                    y={[-20, 10]}
                  />
                </g>
                <g transform={`translate(30,0)`}>
                  <XTicks
                    data={{
                      text: [],
                      lines: [0],
                    }}
                    x={xAxisScale}
                    y={[-20, 10]}
                  />
                </g>
              </g>
            }
          </MaleBars>
          <FemaleBars
            transform={`translate(${size.width / 2 + 30},0)`}
            className='femaleBars'
          >
            <YTicks
              data={{
                text: [],
                lines: getYTicks(maxKey),
              }}
              x={[15, plotArea.left - 2]}
              y={yAxisScale}
            />
            <YTicks
              data={{
                text: [],
                lines: [0],
              }}
              x={[-size.width, plotArea.left - 2]}
              y={yAxisScale}
            />{' '}
            <XTicks
              data={{
                text: getXTicks(xDomain[1]),
                lines: getXTicks(xDomain[1]),
              }}
              x={xAxisScale}
              y={[plotArea.top, plotArea.bottom]}
            />
            {data.length > 0 && yProp && (
              <>
                {data[1]
                  .filter((f) => f.key <= maxKey)
                  .map((bar, i) => {
                    let width = xAxisScale(bar.v) || 0
                    if (width < 0) return null
                    return (
                      <rect
                        key={i}
                        width={width}
                        height={barWidth}
                        y={(yAxisScale(bar.key) || 0) - barWidth / 2}
                        x={0}
                        className={'bar _' + (i % 2)}
                      />
                    )
                  })}
              </>
            )}
            <XTicks
              data={{
                text: [],
                lines: [0],
              }}
              x={xAxisScale}
              y={[plotArea.top - barWidth, plotArea.bottom + barWidth]}
            />
          </FemaleBars>
          {
            // USER MARKER
            markerData.x && (
              <>
                <Marker
                  text={marked.label}
                  x={markerData.x}
                  y={markerData.y || 0}
                  position={
                    markerData.x < size.width * 0.3
                      ? 'right'
                      : markerData.x > size.width * 0.7
                      ? 'left'
                      : 'middle'
                  }
                />
              </>
            )
          }
        </svg>
      </div>
    </StyledBars>
  )
}
const XTicks = ({
  data,
  x,
  y,
}: {
  data: { text?: number[]; lines?: number[] }
  x: ScaleLinear<number, number>
  y: number[]
}) => {
  return (
    <>
      {data.lines &&
        data.lines.map((t, i) => {
          return (
            <line
              key={i}
              y1={y[0]}
              y2={y[1]}
              x1={x(t) || 0}
              x2={x(t) || 0}
              className={'tickline ' + (t === 0 ? 'base' : '')}
            />
          )
        })}
      {data.text &&
        data.text.map((t, i) => {
          return (
            <text
              key={i}
              x={x(t)}
              y={y[1] + textOffset + 13}
              className='ticktext x'
              textAnchor={'middle'}
            >
              {(t + '').replace('-', '')}
            </text>
          )
        })}
    </>
  )
}
const YTicks = ({
  data,
  x,
  y,
}: {
  data: { text?: number[]; lines?: number[] }
  x: number[]
  y: ScaleLinear<number, number>
}) => {
  return (
    <g className='yticks'>
      {data.lines &&
        data.lines.map((t, i) => {
          let height = y(t) || 0
          return (
            <line
              key={i}
              y1={height}
              y2={height}
              x1={-x[0]}
              x2={-x[1]}
              className={'tickline '}
            />
          )
        })}
      {data.text &&
        data.text.map((t, i) => {
          return (
            <text
              key={i}
              x={-x[1] + 30}
              y={(y(t) || 0) + textOffset}
              className='ticktext'
              textAnchor='middle'
            >
              {t}
            </text>
          )
        })}
    </g>
  )
}

const FemaleBars = styled.g`
  rect {
    stroke: var(--site-background);
    stroke-width: 0.5;
    fill: var(--orange-main) !important;
    opacity: 1;
  }
`
const MaleBars = styled.g`
  rect {
    stroke: var(--site-background);
      stroke-width: 0.5;
      fill: var(--petrol-main);

      opacity: 1;
  }
`

const StyledBars = styled.div`
  .femaleBars {
    rect {
      stroke: var(--site-background);
      stroke-width: 0.5;
      fill: var(--orange-main) !important;
      opacity: 1;
    }
  }
  .maleBars {
    rect {
      stroke: var(--site-background);
      stroke-width: 0.5;
      fill: var(--petrol-main);

      opacity: 1;
    }
  }
  svg {
    width: 100%;
    font-family: var(--chart-font-stack);
    font-weight: 200;
    font-size: 0.6em;
    overflow: visible;

    path {
      stroke-width: 2;
      fill: none;
    }
    line.anchorline {
      stroke: #202346;
    }
    line.tickline {
      stroke: #d4d4d4;
      // stroke-width: 0.5;
      &.first {
        stroke: #c8c8d1;
      }
      &.overlay {
        stroke: rgba(32, 35, 70, 0.5);
      }
      &.base {
        stroke: #6f7187;
      }
    }
    line.tickLastValue {
      stroke: #6f7187;
    }
    text.ticktext {
      font-size: 12px;
      fill: #9698a8;
    }
    text.lastVal {
      fill: var(--text-color);
      font-size: 15px;
      font-weight: 700;
    }
    line.ttMarker {
      stroke: #202346;
      fill: none;
      stroke-width: 2;
    }
    text.ttVal {
      font-weight: 700;
      font-size: 15px;
      fill: var(--text-color);

      &.bg {
        stroke: var(--site-background);
        stroke-width: 4;
      }
    }
    text.ttDate {
      font-weight: 300;
      font-size: 12px;
      fill: var(--text-color);

      &.bg {
        stroke: var(--site-background);
        stroke-width: 4;
      }
    }
    circle.lastVal {
      &.hosp {
        fill: #2ca29f;
        stroke: var(--site-background);
      }
      &.intense {
        fill: #d3343f;
        stroke: var(--site-background);
      }
    }
  }
`
